/**
 * Upsell variant dropdown — Cafeshi
 *
 * Native <select> popups open whichever way the browser wants — upward when the
 * control sits near the bottom of the viewport (e.g. the "También compraron"
 * upsell on a short desktop screen). That direction is NOT controllable via CSS.
 *
 * This progressively enhances every [data-upsell-variant] <select> with a custom
 * listbox that opens DOWNWARD (flipping up only when there's genuinely no room).
 * The native <select> stays in the DOM as the source of truth: we set its value
 * and fire a real `change` event, so all existing handlers (iurop-cart-drawer.js
 * price/add-button sync, the PDP add-to-cart reading .value) keep working with no
 * changes. With JS off, the native <select> is simply left visible and usable.
 *
 * The panel is fixed-positioned and appended to <body> so it is never clipped by
 * the cart drawer's own scroll/overflow, and works identically on the PDP.
 */
(function () {
  'use strict';

  var ENHANCED = 'data-cselect-ready';
  var GAP = 4; // px between trigger and panel
  var MARGIN = 8; // viewport breathing room when deciding to flip
  var openState = null; // { select, trigger, panel, options, active }

  var selectedOption = function (select) {
    return select.options[select.selectedIndex] || select.options[0];
  };

  var labelFor = function (select) {
    var o = selectedOption(select);
    return o ? o.textContent.trim() : '';
  };

  // ---- enhancement -------------------------------------------------------
  var enhance = function (select) {
    if (!select || select.getAttribute(ENHANCED)) return;
    if (!select.options || select.options.length === 0) return;
    select.setAttribute(ENHANCED, '1');

    var trigger = document.createElement('button');
    trigger.type = 'button';
    // Reuse the native select's classes so the trigger inherits the pill's
    // padding/font/colour, then add our own reset/affordance class.
    trigger.className = select.className + ' iurop-cselect-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    var aria = select.getAttribute('aria-label');
    if (aria) trigger.setAttribute('aria-label', aria);
    trigger.textContent = labelFor(select);

    select.classList.add('iurop-cselect-native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
    select.parentNode.insertBefore(trigger, select);

    trigger._select = select;
    select._trigger = trigger;

    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (openState && openState.trigger === trigger) closePanel();
      else openPanel(select, trigger);
    });

    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        if (!openState || openState.trigger !== trigger) openPanel(select, trigger);
      }
    });
  };

  // Keep the visible trigger label in sync if the value changes elsewhere
  // (e.g. another script sets select.value and dispatches change).
  document.addEventListener('change', function (e) {
    var select = e.target.closest && e.target.closest('[data-upsell-variant]');
    if (select && select._trigger) select._trigger.textContent = labelFor(select);
  });

  // ---- panel -------------------------------------------------------------
  var buildPanel = function (select) {
    var panel = document.createElement('div');
    panel.className = 'iurop-cselect-panel';
    var list = document.createElement('ul');
    list.className = 'iurop-cselect-panel__list';
    list.setAttribute('role', 'listbox');

    var options = [];
    Array.prototype.forEach.call(select.options, function (opt, i) {
      var li = document.createElement('li');
      li.className = 'iurop-cselect-panel__opt';
      li.setAttribute('role', 'option');
      li.textContent = opt.textContent.trim();
      li.dataset.index = String(i);
      if (opt.disabled) {
        li.classList.add('is-disabled');
        li.setAttribute('aria-disabled', 'true');
      }
      if (i === select.selectedIndex) {
        li.classList.add('is-selected');
        li.setAttribute('aria-selected', 'true');
      }
      list.appendChild(li);
      options.push(li);
    });

    panel.appendChild(list);
    return { panel: panel, options: options };
  };

  var position = function (trigger, panel) {
    var r = trigger.getBoundingClientRect();
    panel.style.minWidth = r.width + 'px';
    // Measure with default (downward) placement first.
    panel.style.top = '-9999px';
    panel.style.left = '-9999px';
    panel.style.visibility = 'hidden';
    var h = panel.offsetHeight;
    var w = panel.offsetWidth;

    var below = window.innerHeight - r.bottom;
    var above = r.top;
    // Open downward by default; flip up only when it won't fit below AND fits
    // (better) above.
    var openUp = below < h + GAP + MARGIN && above > below;
    var top = openUp ? r.top - h - GAP : r.bottom + GAP;

    var left = r.left;
    if (left + w > window.innerWidth - MARGIN) left = Math.max(MARGIN, window.innerWidth - MARGIN - w);

    panel.style.top = Math.max(MARGIN, top) + 'px';
    panel.style.left = left + 'px';
    panel.style.visibility = '';
    panel.classList.toggle('is-up', openUp);
  };

  var openPanel = function (select, trigger) {
    closePanel();
    var built = buildPanel(select);
    var panel = built.panel;
    document.body.appendChild(panel);
    position(trigger, panel);

    trigger.setAttribute('aria-expanded', 'true');
    var active = select.selectedIndex >= 0 ? select.selectedIndex : 0;
    openState = { select: select, trigger: trigger, panel: panel, options: built.options, active: active };
    setActive(active);

    panel.addEventListener('click', function (e) {
      var li = e.target.closest('.iurop-cselect-panel__opt');
      if (!li || li.classList.contains('is-disabled')) return;
      choose(parseInt(li.dataset.index, 10));
    });
  };

  var closePanel = function () {
    if (!openState) return;
    if (openState.panel && openState.panel.parentNode) openState.panel.parentNode.removeChild(openState.panel);
    openState.trigger.setAttribute('aria-expanded', 'false');
    openState = null;
  };

  var setActive = function (index) {
    if (!openState) return;
    var opts = openState.options;
    if (index < 0) index = 0;
    if (index > opts.length - 1) index = opts.length - 1;
    openState.active = index;
    opts.forEach(function (li, i) { li.classList.toggle('is-active', i === index); });
    var el = opts[index];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  };

  // Move to the next/previous option that isn't disabled.
  var step = function (dir) {
    if (!openState) return;
    var opts = openState.options;
    var i = openState.active;
    for (var n = 0; n < opts.length; n++) {
      i += dir;
      if (i < 0 || i > opts.length - 1) return;
      if (!opts[i].classList.contains('is-disabled')) { setActive(i); return; }
    }
  };

  var choose = function (index) {
    if (!openState) return;
    var select = openState.select;
    if (index !== select.selectedIndex && !select.options[index].disabled) {
      select.selectedIndex = index;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      if (select._trigger) select._trigger.textContent = labelFor(select);
    }
    var trigger = openState.trigger;
    closePanel();
    if (trigger) trigger.focus();
  };

  // ---- global listeners --------------------------------------------------
  document.addEventListener('keydown', function (e) {
    if (!openState) return;
    if (e.key === 'Escape') { e.preventDefault(); var t = openState.trigger; closePanel(); t.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); step(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); step(-1); }
    else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); choose(openState.active); }
    else if (e.key === 'Tab') { closePanel(); }
  });

  document.addEventListener('click', function (e) {
    if (openState && !e.target.closest('.iurop-cselect-panel') && e.target !== openState.trigger) closePanel();
  });
  // A scroll/resize moves the anchor — simplest correct behaviour is to close.
  window.addEventListener('scroll', closePanel, true);
  window.addEventListener('resize', closePanel);
  // The cart drawer re-renders its rows (and our triggers) on every update.
  document.addEventListener('cart:updated', closePanel);

  // ---- bootstrap + dynamic content --------------------------------------
  var scan = function (root) {
    var nodes = (root || document).querySelectorAll('[data-upsell-variant]:not([' + ENHANCED + '])');
    Array.prototype.forEach.call(nodes, enhance);
  };

  var ready = function () {
    scan(document);
    // Upsell rows are injected/replaced dynamically (cart drawer refresh, PDP
    // spare swaps), so enhance new selects as they appear.
    if (window.MutationObserver) {
      var obs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches('[data-upsell-variant]')) enhance(node);
            else if (node.querySelector) scan(node);
          }
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
})();
